/* ──────────────────────  TodayStats.jsx (Compact) ────────────────────── */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Card, CardContent, Stack, Typography, Chip, Divider, IconButton,
  CircularProgress
} from '@mui/material';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import { CheckCircle, CurrencyRupee, Refresh } from '@mui/icons-material';

/* ────── LUXURY THEME (unchanged) ────── */
const theme = createTheme({
  palette: {
    primary: { main: '#d4af37' },
    secondary: { main: '#b8932b' },
    background: { default: '#f8f8f8', paper: '#ffffff' },
    text: { primary: '#1a1a1a', secondary: '#555555' },
  },
  typography: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    h6: { fontWeight: 700 },
    body2: { fontWeight: 500 },
  },
  shape: { borderRadius: 28 },
});

/* ────── LUXURY CARD (compact) ────── */
const LuxuryCard = styled(Card)(({ theme }) => ({
  background: '#ffffff',
  border: '1px solid rgba(212,175,55,0.3)',
  borderRadius: 24,
  /** Tight padding – saves vertical space **/
  padding: '16px 18px',
  boxShadow: '0 8px 20px rgba(0,0,0,0.07)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 12px 28px rgba(0,0,0,0.1)',
    transform: 'translateY(-2px)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: 4,
    background: 'linear-gradient(90deg, #d4af37, #b8932b)',
    borderRadius: '24px 24px 0 0',
  },
}));

/* ────── GOLD TEXT ────── */
const GoldText = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(90deg, #d4af37, #b8932b)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  fontWeight: 800,
}));

export default function TodayStats() {
  const [stats, setStats] = useState({ count: 0, total: 0 });
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem('admin_token');
  const api = process.env.REACT_APP_API_URL;

  const fetchStats = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${api}/admin-today-stats.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load today stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <LuxuryCard>
        <CardContent sx={{ pt: 0, pb: 1 }}>
          <Stack spacing={2}> {/* tight spacing */}

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckCircle sx={{ fontSize: 28, color: '#d4af37' }} />
                <GoldText variant="h6" sx={{ fontSize: '1.1rem' }}>
                  Today’s Orders
                </GoldText>
              </Stack>

              <IconButton
                onClick={fetchStats}
                disabled={loading}
                size="small"
                sx={{
                  bgcolor: 'rgba(212,175,55,0.12)',
                  '&:hover': { bgcolor: 'rgba(212,175,55,0.2)' },
                  color: '#d4af37',
                  width: 36,
                  height: 36,
                }}
              >
                {loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Refresh sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Box>

            <Divider sx={{ borderColor: 'rgba(212,175,55,0.25)' }} />

            {/* Stats – compact rows */}
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                  Completed
                </Typography>
                <GoldText variant="h5" sx={{ fontSize: '1.7rem' }}>
                  {stats.count}
                </GoldText>
              </Stack>

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                  Revenue
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: '1.7rem',
                    fontWeight: 800,
                    color: '#006600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.3,
                  }}
                >
                  <CurrencyRupee sx={{ fontSize: '0.7em' }} />
                  {Number(stats.total).toFixed(0)}
                </Typography>
              </Stack>
            </Stack>

            {/* Tiny chip */}
            <Chip
              icon={<CurrencyRupee fontSize="small" />}
              label={loading ? '…' : 'Live'}
              size="small"
              sx={{
                bgcolor: 'rgba(212,175,55,0.12)',
                color: '#d4af37',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 22,
                alignSelf: 'flex-start',
                borderRadius: 12,
              }}
            />
          </Stack>
        </CardContent>
      </LuxuryCard>

      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </ThemeProvider>
  );
}